import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The small controls the points designer is built from.
 *
 * They live here rather than in either app because the builder is shared, and they are written
 * against the *direction tokens* — `surface`, `line`, `ink`, `felt`, `bad`, `warn` — rather than
 * literal colours, so the same component reads as The Ledger in admin-web and The Rail in
 * room-web without a branch. Nothing here is claimed as a general design system; it is the
 * vocabulary this one screen needs.
 */

// ---------------------------------------------------------------- structure

/**
 * One numbered section of the definition. The number matters: §9.2 fixes the order, and an
 * operator working through the form for the first time is following a sequence, not browsing a
 * settings screen.
 */
export function BuilderSection({
  step,
  title,
  summary,
  children,
  action,
  open,
  onToggle,
  badge,
}: {
  step: number;
  title: string;
  /** What this section currently says, in one line, for reading without opening anything. */
  summary?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  /**
   * Whether the body is showing. <b>Omit for a section that is always open</b>, which is what the
   * points designer's eight sections are: passing `undefined` leaves the header inert and renders
   * the children, so every existing caller is untouched.
   *
   * Ratings §9.2 asks for collapsible sections with a summary line, because a rating board is five
   * documents and a screen showing all of them at once is one nobody can find anything in. The
   * summary is what makes that safe — a closed section that says nothing is a section whose
   * contents have to be opened to be checked.
   */
  open?: boolean;
  onToggle?: () => void;
  /** A count, a warning marker — something that has to be visible while the section is closed. */
  badge?: ReactNode;
}) {
  const collapsible = open !== undefined && onToggle !== undefined;

  const heading = (
    <>
      <h3 className="flex items-baseline gap-2 font-display text-sm font-semibold text-ink-950">
        <span className="num text-[11px] font-normal text-ink-800/40">{step}</span>
        {title}
        {badge ? <span className="ml-1">{badge}</span> : null}
      </h3>
      {summary ? <p className="mt-0.5 text-xs text-ink-800/55">{summary}</p> : null}
    </>
  );

  return (
    <section className="border-b border-line pb-6 last:border-b-0">
      <header
        className={`flex flex-wrap items-start justify-between gap-x-4 gap-y-1 ${
          collapsible && !open ? '' : 'mb-4'
        }`}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
          >
            <span
              aria-hidden
              className={`mt-1 shrink-0 text-[10px] text-ink-800/40 transition-transform ${
                open ? 'rotate-90' : ''
              }`}
            >
              ▶
            </span>
            <span className="min-w-0">{heading}</span>
          </button>
        ) : (
          <div className="min-w-0">{heading}</div>
        )}
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>
      {/* Unmounted rather than hidden while closed: these bodies hold their own local state — an
          open tag row, a search box — and keeping a dozen of them mounted to preserve state nobody
          returns to is the wrong trade. Everything that matters lives in the draft above. */}
      {collapsible && !open ? null : children}
    </section>
  );
}

export function Labelled({
  label,
  hint,
  className = '',
  children,
}: {
  label: string;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="mb-1 block text-xs text-ink-800/60">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-ink-800/45">{hint}</span> : null}
    </label>
  );
}

/**
 * Worked examples and consequences, §9.5's habit. Distinct from a hint under a field: this is a
 * sentence about the operator's own numbers, and it is the thing that turns a band table into
 * something judgeable.
 */
export function Worked({ children }: { children: ReactNode }) {
  return (
    <p className="border-l-2 border-felt-700/25 pl-2.5 text-[11.5px] leading-relaxed text-ink-800/60">
      {children}
    </p>
  );
}

/**
 * The shape `IssueList` renders.
 *
 * Structural rather than `DefinitionIssue`, because the salary model designer produces its own
 * issue type with its own section union and renders it through this same component — the two
 * validators answer about different documents and there is nothing to gain from one union naming
 * both documents' sections.
 */
export interface SectionIssue {
  severity: 'error' | 'warning';
  message: string;
  rowIndex?: number;
}

/** Errors and warnings for one section, in the section. Errors first — they block the save. */
export function IssueList({
  issues,
  className = '',
}: {
  issues: readonly SectionIssue[];
  className?: string;
}) {
  if (issues.length === 0) return null;

  const ordered = [...issues].sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));

  return (
    <ul className={`space-y-1 ${className}`}>
      {ordered.map((issue, i) => (
        <li
          key={`${issue.severity}-${issue.rowIndex ?? 'x'}-${i}`}
          className={`flex gap-1.5 text-[11.5px] ${
            issue.severity === 'error' ? 'text-bad' : 'text-warn'
          }`}
        >
          <span aria-hidden>{issue.severity === 'error' ? '✕' : '!'}</span>
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- inputs

export const controlClass =
  'border border-line bg-surface px-2.5 py-1.5 text-sm text-ink-950 outline-none focus:border-felt-500 disabled:bg-ground disabled:text-ink-800/50';

export const inputClass = `w-full ${controlClass}`;

/**
 * A number box where empty is a state rather than an accident.
 *
 * Binding an input straight to a numeric field turns "clear the box and type 15" into a moment
 * where the value is NaN, and a half-typed `1.` into a value that snaps back to `1` and eats the
 * decimal point. So the text is held locally and only pushed up when it parses; the value coming
 * back down replaces the text only when it disagrees with what is on screen, which lets a preset
 * overwrite the box without fighting somebody mid-keystroke.
 */
export function NumberInput({
  value,
  onChange,
  ariaLabel,
  className = '',
  placeholder,
  min,
  step,
  disabled = false,
  invalid = false,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  ariaLabel: string;
  className?: string;
  placeholder?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const [text, setText] = useState(() => (value === null ? '' : String(value)));
  const latest = useRef(text);
  latest.current = text;

  useEffect(() => {
    if (parseNumber(latest.current) !== value) setText(value === null ? '' : String(value));
  }, [value]);

  return (
    <input
      type="number"
      inputMode="decimal"
      value={text}
      min={min}
      step={step}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseNumber(e.target.value));
      }}
      className={`num ${controlClass} ${invalid ? 'border-bad' : ''} ${className}`}
    />
  );
}

function parseNumber(text: string): number | null {
  if (text.trim() === '') return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

// ---------------------------------------------------------------- choices

/**
 * The Placement table / Expression switch of §9.2.
 *
 * A disabled option is shown rather than hidden: an expression system is a thing this designer
 * will do, and leaving it off the control entirely would read as "tables are all there is".
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: readonly { value: T; label: string; disabled?: boolean; title?: string }[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex border border-line bg-surface p-0.5">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            title={option.title}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.06em] transition ${
              active
                ? 'bg-felt-700 text-white'
                : option.disabled
                  ? 'cursor-not-allowed text-ink-800/30'
                  : 'text-ink-800/70 hover:text-ink-950'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A stack of radio options, each carrying the sentence that says what it does. */
export function ChoiceList<T extends string>({
  name,
  options,
  value,
  onChange,
  disabled = false,
}: {
  name: string;
  options: readonly { value: T; label: string; detail: string }[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <label
            key={option.value}
            className={`flex cursor-pointer gap-2.5 border p-3 transition ${
              active ? 'border-felt-700 bg-felt-50/60' : 'border-line bg-surface hover:border-ink-800/25'
            } ${disabled ? 'cursor-default opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={name}
              checked={active}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              className="mt-0.5 accent-[var(--color-felt-700)]"
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium text-ink-950">{option.label}</span>
              <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-800/60">
                {option.detail}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function CheckboxRow({
  checked,
  onChange,
  label,
  detail,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  detail?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={`flex gap-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-[var(--color-felt-700)]"
      />
      <span className="min-w-0">
        <span className="block text-[13px] text-ink-950">{label}</span>
        {detail ? (
          <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-800/60">{detail}</span>
        ) : null}
      </span>
    </label>
  );
}

/** A small secondary action — add a row, apply a preset, remove a band. */
export const smallButtonClass =
  'border border-line bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-800/70 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-40';

export const primaryButtonClass =
  'bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-50';

export const ghostButtonClass =
  'border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Status chips: draft, published, archived, platform.
 *
 * <b>The content must be a STATE, never an action — and that is a rule about this component's
 * costume rather than about grammar.</b> A `neutral` Pill is `border-line bg-surface` with
 * uppercase bold tracking in muted ink, which is `smallButtonClass` to within a couple of pixels;
 * and `smallButtonClass` disabled is that same styling at 40% opacity. So a Pill whose label reads
 * as a verb is indistinguishable from a greyed-out secondary button, and a reader's only available
 * conclusion is that the product is refusing them.
 *
 * It cost a bug report: `<Pill tone="neutral">Preview</Pill>` in the rating builder's preview-pane
 * header, beside a caption asking the operator to act, was read as a dead PREVIEW button — and
 * "disabled in every state I can reach" is exactly right about a `<span>`. Every other Pill in the
 * product is a noun or an adjective (Archived, Published, Final, Paused, Room default, Not counted,
 * Testing only); that one was the only verb.
 *
 * If a panel needs to say what it is, say it in words. If it needs a control, use a button.
 */
export function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'strong';
  children: ReactNode;
}) {
  const tones: Record<string, string> = {
    neutral: 'border-line bg-surface text-ink-800/65',
    good: 'border-good/35 bg-good/10 text-good',
    warn: 'border-warn/35 bg-warn/10 text-warn',
    bad: 'border-bad/35 bg-bad/10 text-bad',
    strong: 'border-transparent bg-felt-700 text-white',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
